import Popup from 'a11y/Popup';
import Menu from 'a11y/Menu';
import MenuItem from 'a11y/MenuItem';
import keyCodes from 'a11y/lib/keyCodes';
import breakpoints from 'utils/breakpoints';
import getElementFromComponent from 'utils/getElementFromComponent';
import getClassnameFromComponent from 'utils/getClassnameFromComponent';

/**
 * Component for the NavBar menu.
 */
export default class NavBar {
  /**
   * Start the component
   */
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    const { menuStylesheet, persist } = this.options;

    /**
     * Test for the header variant for which the menu button persists across breakpoints.
     * @type {boolean}
     */
    this.persistMenuButton = (persist === true);

    /**
     * Hold the `.menu` class instances.
     * @type {Menu|MenuItem}
     */
    this.primaryMenu = {};

    /**
     * Hold the `.topics-menu` class instance.
     * @type {MenuItem}
     */
    this.topicsMenu = {};

    /**
     * Hold the `.topics-menu__list` class instance.
     * @type {MenuItem}
     */
    this.topicsSubMenu = {};

    const stylesheet = (undefined !== menuStylesheet)
      ? menuStylesheet
      : 'primary-menu';

    const { toggle, navBar, actions } = this.children;

    // The primary navigation.
    const menu = getElementFromComponent('menu', stylesheet);

    // Get the submenu parent links.
    const hasChildren = getClassnameFromComponent('has-children', stylesheet);
    const submenuLinks = menu.querySelectorAll(`${hasChildren} > a`);

    // The (mobile-only) Topics Menu
    const topics = getElementFromComponent('topics-menu', stylesheet);
    const topicsList = getElementFromComponent('topics-menu__list', stylesheet);
    // The (desktop-only) Topics MegaMenu
    const topicsMegaMenu = getElementFromComponent('list', 'topics-megamenu');

    // Collect instance properties.
    Object.assign(this, {
      toggle,
      navBar,
      menu,
      topics,
      topicsList,
      actions,
      topicsMegaMenu,
      submenuLinks,
      menuButton: {},
    });

    // Bind class methods
    this.handleBreakpointChanges = this.handleBreakpointChanges.bind(this);
    this.setUpMenuButton = this.setUpMenuButton.bind(this);
    this.useHamburgerMenu = this.useHamburgerMenu.bind(this);
    this.setUpPrimaryMenu = this.setUpPrimaryMenu.bind(this);
    this.setUpPrimaryMenuItem = this.setUpPrimaryMenuItem.bind(this);
    this.setUpSecondaryMenuItems = this.setUpSecondaryMenuItems.bind(this);
    this.setUpSecondaryMenu = this.setUpSecondaryMenu.bind(this);
    this.addKeydownPatches = this.addKeydownPatches.bind(this);
    this.removeKeydownPatches = this.removeKeydownPatches.bind(this);
    this.patchTopLevelKeydown = this.patchTopLevelKeydown.bind(this);
    this.patchNavBarKeydown = this.patchNavBarKeydown.bind(this);
    this.patchMenuButtonKeydown = this.patchMenuButtonKeydown.bind(this);
    this.patchPrimaryMenuFirstItem = this.patchPrimaryMenuFirstItem.bind(this);
    this.patchTopicsMenuFirstItem = this.patchTopicsMenuFirstItem.bind(this);

    if (this.persistMenuButton) {
      // Use the menu button setup (mobile menu) across all breakpoints.
      this.useHamburgerMenu();
    } else {
      // Check the viewport width and initialize accordingly.
      this.handleBreakpointChanges();
      breakpoints.lgMin.addListener(this.handleBreakpointChanges);
    }
  }

  /**
   * The Menu Button Popup should only be initialized for small screens.
   */
  handleBreakpointChanges() {
    if (breakpoints.lgMin.matches) {
      // Instantiate `this.primaryMenu` as Menu.
      this.setUpPrimaryMenu();

      // Destroy `this.topicsMenu` MenuItem.
      if (this.topicsMenu instanceof MenuItem) {
        this.topicsMenu.destroy();
      }

      // Destroy the menu button for large screens.
      if (this.menuButton instanceof Popup) {
        this.menuButton.destroy();
      }

      // Remove small screen keyboard support as not needed for larger screens.
      this.removeKeydownPatches();

      // Focus the topics megaMenu if a topics item has focus.
      if (
        this.topics !== null
        && this.topics.contains(document.activeElement)
      ) {
        this.topicsMegaMenu.menu.firstItem.focus();
      }
    } else {
      this.useHamburgerMenu();
    }
  }

  /**
   * Set up the menu button as a Popup.
   */
  setUpMenuButton() {
    // The popup may be been instantiated, but subsequently destroyed.
    if (this.menuButton instanceof Popup) {
      // If the Popup class has already been initialized, reset it.
      this.menuButton.setup();
    } else {
      // Initialize the Popup class.
      this.menuButton = new Popup({
        controller: this.toggle,
        target: this.navBar,
      });
    }

    /**
     * Adds expected keyboard support to the menu button toggle.
     *
     * @param {Event} event
     */
    this.menuButton.controller.addEventListener(
      'keydown',
      this.patchMenuButtonKeydown,
    );
  }

  /**
   * Set up the primary nav menu as a Menu for desktop menu.
   * The primary menu may be been instantiated as Menu, but subsequently destroyed.
   */
  setUpPrimaryMenu() {
    if (this.primaryMenu instanceof MenuItem) {
      this.primaryMenu.destroy();
    }

    if (this.primaryMenu instanceof Menu) {
      // If the Menu class has already been initialized, reset it.
      this.primaryMenu.init();
    } else {
      // Initialize the Menu class.
      this.primaryMenu = new Menu(this.menu);
    }
  }

  /**
   * Set up the primary nav menu as a MenuItem for mobile menu.
   * The primary menu may be been instantiated as MenuItem, but subsequently destroyed.
   */
  setUpPrimaryMenuItem() {
    if (this.primaryMenu instanceof Menu) {
      this.primaryMenu.destroy();
    }

    if (this.primaryMenu instanceof MenuItem) {
      // If the MenuItem class has already been initialized, reset it.
      this.primaryMenu.init();
    } else {
      // Initialize the MenuItem class.
      this.primaryMenu = new MenuItem(this.menu);
    }
  }

  /**
   * Set up the Topics menu as MenuItems for mobile menu.
   * Either of these may have been instantiated, but subsequently destroyed.
   */
  setUpSecondaryMenuItems() {
    if (this.topics !== null) {
      if (this.topicsMenu instanceof MenuItem) {
        // If the MenuItem class has already been initialized, reset it.
        this.topicsMenu.init();
      } else {
        // Initialize the MenuItem class for the Topics menu.
        this.topicsMenu = new MenuItem(this.topics);
      }
    }
  }

  /**
   * Set up the Topics menu as a Menu for mobile.
   * Either of these may have been instantiated, but subsequently destroyed.
   */
  setUpSecondaryMenu() {
    if (this.topicsMenu instanceof MenuItem) {
      this.topicsMenu.destroy();
    }

    if (this.topicsList !== null) {
      if (this.topicsSubMenu instanceof Menu) {
        // If the Menu class has already been initialized, reset it.
        this.topicsSubMenu.init();
      } else {
        // Initialize the Menu class for the Topics menu.
        this.topicsSubMenu = new Menu(this.topicsList);
      }
    }
  }

  /**
   * Set the menu hidden behind the menu button.
   */
  useHamburgerMenu() {
    // Set up the menu button as a Popup.
    this.setUpMenuButton();

    // Instantiate `this.primaryMenu` as MenuItem.
    this.setUpPrimaryMenuItem();

    // Instantiate `this.topicsMenu` as MenuItems.
    this.setUpSecondaryMenuItems();

    // Instantiate `this.topicsSubMenu` as a Menu.
    this.setUpSecondaryMenu();

    // Patch keyboard support.
    this.addKeydownPatches();

    // Focus the menuButton if a nav-bar child has focus.
    if (
      this.navBar.contains(document.activeElement)
      || (
        this.topics !== null
        && this.topics.contains(document.activeElement)
      )
    ) {
      this.menuButton.controller.focus();
    }
  }

  /**
   * Remove keydown patches for large screen.
   */
  removeKeydownPatches() {
    if (
      this.primaryMenu instanceof MenuItem
      || this.primaryMenu instanceof Menu
    ) {
      this.primaryMenu.firstItem.removeEventListener(
        'keydown',
        this.patchPrimaryMenuFirstItem,
      );
    }

    if (this.topicsMenu instanceof MenuItem) {
      this.topicsMenu.firstItem.removeEventListener(
        'keydown',
        this.patchTopicsMenuFirstItem,
      );
    }

    this.menu.removeEventListener('keydown', this.patchTopLevelKeydown);
    if (this.topics !== null) {
      this.topics.removeEventListener('keydown', this.patchTopLevelKeydown);
    }

    this.navBar.removeEventListener('keydown', this.patchNavBarKeydown);
  }

  /**
   * Patch incomplete keyboard navigation. Applied to individual elements,
   * rather than delegated, in order to override the Menu/MenuItem event
   * listeners.
   */
  addKeydownPatches() {
    // Arrow up key moves focus to the topics menu's first item.
    this.primaryMenu.firstItem.addEventListener(
      'keydown',
      this.patchPrimaryMenuFirstItem,
    );

    // Patches incomplete Topics Menu keyboard navigation support.
    if (this.topics !== null) {
      this.topicsMenu.firstItem.addEventListener(
        'keydown',
        this.patchTopicsMenuFirstItem,
      );
    }

    // Close the menu on left arrow key.
    this.menu.addEventListener('keydown', this.patchTopLevelKeydown);
    if (this.topics !== null) {
      this.topics.addEventListener('keydown', this.patchTopLevelKeydown);
    }

    // Patch incomplete keyboard navigation.
    this.navBar.addEventListener('keydown', this.patchNavBarKeydown);
  }

  /**
   * Patches incomplete keyboard navigation. These are separate lists, so not
   * covered by the MenuItem's default keydown handlers:
   *
   * - Closes the menu on left arrow key if the item isn't a submenu.
   *
   * @param {Event} event
   */
  patchTopLevelKeydown(event) {
    const { keyCode, target } = event;
    const { LEFT } = keyCodes;

    if (
      LEFT === keyCode
      && (
        undefined === target.menuItem
        || undefined === target.menuItem.previousSibling
      )
    ) {
      this.menuButton.setExpandedState(false);
      this.menuButton.controller.focus();
    }
  }

  /**
   * Patches incomplete keyboard navigation. These are separate lists, so not
   * covered by the MenuItem's default keydown handlers:
   *
   * - Home key moves to last menu item, End key moves to last menu item.
   *
   * @param {Event} event
   */
  patchNavBarKeydown(event) {
    const { keyCode } = event;
    const { HOME, END } = keyCodes;

    if (HOME === keyCode) {
      event.preventDefault();
      this.menuButton.firstItem.focus();
    } else if (END === keyCode) {
      event.preventDefault();
      this.menuButton.lastItem.focus();
    }
  }

  /**
   * Patches incomplete keyboard navigation. These are separate lists, so not
   * covered by the MenuItem's default keydown handlers:
   *
   * - Moves from Topics Menu to Primary Menu.
   * - Recycles keyboard navigation from first to last item
   *
   * @param {Event} event
   */
  patchTopicsMenuFirstItem(event) {
    const { keyCode } = event;
    const { UP, DOWN } = keyCodes;

    if (DOWN === keyCode && undefined !== this.primaryMenu.firstItem) {
      event.preventDefault();
      this.primaryMenu.firstItem.focus();
    } else if (UP === keyCode) {
      event.preventDefault();
      this.menuButton.lastItem.focus();
    }
  }

  /**
   * Patches incomplete keyboard navigation. These are separate lists, so not
   * covered by the MenuItem's default keydown handlers:
   *
   * - Moves from the Primary Menu to the Topics Menu.
   *
   * @param {Event} event
   */
  patchPrimaryMenuFirstItem(event) {
    const { keyCode } = event;
    const { UP } = keyCodes;

    if (UP === keyCode && undefined !== this.topicsMenu.firstItem) {
      event.preventDefault();
      this.topicsMenu.firstItem.focus();
    }
  }

  /**
   * Patches incomplete keyboard navigation. These are separate lists, so not
   * covered by the MenuItem's default keydown handlers:
   *
   * - Opens the menu and selects the appropriate item
   *
   * @param {Event} event
   */
  patchMenuButtonKeydown(event) {
    const { keyCode } = event;
    const { UP, DOWN } = keyCodes;

    if ([UP, DOWN].includes(keyCode)) {
      event.preventDefault();

      // Open the menu if it's not already open.
      if (!this.menuButton.state.expanded) {
        this.menuButton.setExpandedState(true);
      }

      if (UP === keyCode) {
        // Focus the last item.
        this.menuButton.lastItem.focus();
      } else {
        // Focus the first item (down arrow).
        this.menuButton.firstItem.focus();
      }
    }
  }
}
